<template>
    <div :class="this.mod ? `${this.mod}-custom-select`: ''" class="custom-select" ref="wrapper">
        <div v-bind="attributes"
             :class="selectedValueClasses"
             :id="id"
             tabindex="0"
             ref="selected"
             @focus="onFocus"
             @blur="onBlur"
             @keydown="onKeyDown"
             @keyup="onKeyUp"
             @mousedown="onMouseDown"
             @mouseup="onMouseUp"
        >{{ selectedLabel }}</div>

        <div v-if="showList" class="custom-select-dropdown" ref="list">
            <ul>
                <li v-for="option in options"
                    :key="option.value"
                    :class="optionClasses(option)"
                    :ref="`item-${option.value}`"
                    @click.prevent="onItemSelect($event, option)"
                >{{ option.label }}</li>
            </ul>
        </div>
    </div>
</template>

<script>
    export default {
        name: 'custom-select',

        props: {
            value: null,

            id: String,

            mod: String,

            required: {
                type: Boolean,
                default: () => false,
            },

            placeholder: String,

            attributes: {
                type: Object,
                default: () => { return {}; },
            },

            options: {
                type: Array,
                required: true,
                validator: (value) => {
                    for (let i in value) {
                        if (value.hasOwnProperty(i)) {
                            if (typeof value[i] !== 'object') {
                                return false;
                            }

                            if (value[i].value === undefined || value[i].label === undefined) {
                                return false;
                            }
                        }
                    }

                    return true;
                }
            }
        },

        mounted() {
            document.addEventListener('click', this.onDocumentClick, false);
        },

        data() {
            return {
                showList: false,
                highlighted: null,
                mouseDown: false,
                targetCodes: ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Enter', 'Space'],
            };
        },

        computed: {
            selectedValueClasses() {
                let classes = ['form-control', 'dropdown'];

                if (!this.value) {
                    classes.push('placeholder');
                }

                if (this.attributes && this.attributes.class) {
                    classes.push(this.attributes.class);
                }

                return classes;
            },

            selectedLabel() {
                const selectedValue = this.value;

                const option = this.options.find((option) => {
                    return option.value === selectedValue;
                });

                return option ? option.label : this.placeholder;
            },
        },

        methods: {
            optionClasses(option) {
                let classes = [];

                if (this.value === option.value) {
                    classes.push('active');
                }

                if (this.highlighted === option.value) {
                    classes.push('highlight');
                }

                if (option.value === null) {
                    classes.push('placeholder');
                }

                return classes;
            },

            onDocumentClick(e) {
                // Error prevention.
                if (this.$refs.wrapper === undefined) {
                    return;
                }

                // If the clicked element isn't within the container, close the list.
                if (!this.$refs.wrapper.contains(e.target)) {
                    // Don't close the list if this is a label for this custom select.
                    if (e.target.tagName.toLowerCase() === 'label' && e.target.htmlFor === this.id) {
                        return;
                    }

                    this.closeList();
                }
            },

            onItemSelect(e, option) {
                this.$emit('input', option.value);

                this.closeList();
            },

            focus() {
                this.$refs.selected.focus();
            },

            onFocus() {
                this.onMouseUp();
            },

            onBlur(e) {
                if (e.relatedTarget !== null) {
                    this.closeList();
                }
            },

            onMouseDown() {
                this.mouseDown = true;
            },

            onMouseUp() {
                if (this.showList && this.mouseDown) {
                    this.mouseDown = false;

                    this.closeList();
                } else {
                    this.mouseDown = false;

                    this.openList();
                }
            },

            onKeyDown(e) {
                // Tabbing out?
                if (window._.includes(['Tab'], e.code)) {
                    this.closeList();
                } else if (window._.includes(this.targetCodes, e.code)) {
                    e.preventDefault();
                }
            },

            onKeyUp(e) {
                switch (e.code) {
                    case 'ArrowUp': case 'ArrowLeft':
                        this.highlightPreviousOption();
                        break;

                    case 'ArrowDown': case 'ArrowRight':
                        this.highlightNextOption();
                        break;

                    case 'Enter': case 'Space':
                        this.$emit('input', this.highlighted);
                        this.closeList();
                        this.$refs.selected.blur();
                        break;

                    case 'Escape':
                        this.closeList();
                        break;
                }
            },

            openList() {
                this.showList = true;

                this.highlighted = this.value;

                this.$nextTick(() => {
                    if (this.highlighted !== null) {
                        const box = this.$refs.list.offsetHeight + this.$refs.list.scrollTop;

                        const item = this.$refs[`item-${this.highlighted}`][0];

                        const itemBottom = item.offsetHeight + item.offsetTop;

                        if (itemBottom > box) {
                            this.$refs.list.scrollTop = this.$refs.list.scrollTop + (itemBottom - box);
                        }
                    }
                });
            },

            closeList() {
                this.showList = false;
            },

            highlightNextOption() {
                let highlighted = null, i, takeNext = false;

                for (i in this.options) {
                    if (this.options.hasOwnProperty(i)) {
                        if (takeNext === true) {
                            highlighted = this.options[i].value;
                            break;
                        }

                        if (this.highlighted === this.options[i].value) {
                            takeNext = true;
                        }
                    }
                }

                if (highlighted !== null) {
                    this.highlighted = highlighted;

                    const box = this.$refs.list.offsetHeight + this.$refs.list.scrollTop;

                    const item = this.$refs[`item-${highlighted}`][0];

                    const itemBottom = item.offsetHeight + item.offsetTop;

                    if (itemBottom > box) {
                        this.$refs.list.scrollTop = this.$refs.list.scrollTop + (itemBottom - box);
                    }
                }
            },

            highlightPreviousOption() {
                let highlighted = null, i;

                for (i in this.options) {
                    if (this.options.hasOwnProperty(i)) {
                        if (this.highlighted !== this.options[i].value) {
                            highlighted = this.options[i].value;
                        } else {
                            break;
                        }
                    }
                }

                if (highlighted === null) {
                    highlighted = window._.first(this.options).value;
                } else {
                    const item = this.$refs[`item-${highlighted}`][0];

                    if (item.offsetTop < this.$refs.list.scrollTop) {
                        this.$refs.list.scrollTop = item.offsetTop;
                    }
                }

                this.highlighted = highlighted;
            },
        },
    }
</script>
